import { SKINS } from "../skin";

// Fixtures mirror what popup-container.jsx derives for a real install, so a
// demo frame shows exactly what that kind of user sees:
//   isProFeaturesLocked = !isPro && !trialMode && featureUsage.available !== true
//   skin = "color" while locked (the stored preference is ignored), else the
//          stored preference (default "color")
import { billingPageUrl, billingView } from "../../pro/billing";

// The Billing sheet's purchase for a demo frame: a lifetime Pro, as
// payhook-api's GET /v1/payment_hooks/{id}/billing answers it.
export const DEMO_BILLING = {
  status: "ready",
  view: billingView(
    [
      {
        id: "pay_demo",
        product: "Slowed Pro",
        mode: "payment",
        status: "active",
        amount: 1999,
        currency: "usd",
        paidAt: "2026-09-12T15:00:00.000Z",
        email: "you@example.com",
        paymentMethod: "card",
        card: { brand: "visa", last4: "4242" },
        receiptUrl: "https://pay.stripe.com/receipts/demo",
      },
    ],
    { locale: "en-US" },
  ),
  billingUrl: billingPageUrl({ accountId: "acct_1JENvdEahV87HJ74" }),
};

export const DEMO_TIERS = {
  unlocked: {
    isPro: false,
    isProFeaturesLocked: false,
    trialMode: false,
    remainingTrialDays: 0,
  },
  // Trial over, free metered minutes still left: Pro features stay open
  // (usage available), and the Unlock PRO pill rotates through the meter.
  // Skins stay pickable here, so "mono" (Mono) is a real stored choice; pass
  // ?skin=color for a default install.
  free: {
    isPro: false,
    isProFeaturesLocked: false,
    trialMode: false,
    trialEnded: true,
    remainingTrialDays: 0,
    skin: "mono",
    usageAvailable: true,
    usageHit: false,
    usageRemaining: 100,
    usagePeriod: "weekly",
    usageProgress: 0.65,
  },
  // Free minutes used up: Pro features lock, the skin is forced back to the
  // default, and the pill shows the depleted state. Slowing down stays free.
  capped: {
    isPro: false,
    isProFeaturesLocked: true,
    trialMode: false,
    trialEnded: true,
    remainingTrialDays: 0,
    skin: "color",
    usageAvailable: false,
    usageHit: true,
    usageRemaining: 0,
    usagePeriod: "weekly",
    usageProgress: 1,
  },
  trial: {
    isPro: false,
    isProFeaturesLocked: false,
    trialMode: true,
    remainingTrialDays: 5,
  },
  pro: {
    isPro: true,
    isProFeaturesLocked: false,
    trialMode: false,
    remainingTrialDays: 0,
  },
  // Pro bought once: Manage plan opens the Billing sheet with the purchase.
  lifetime: {
    isPro: true,
    isProFeaturesLocked: false,
    trialMode: false,
    remainingTrialDays: 0,
    hideManagePlan: true,
    billing: DEMO_BILLING,
  },
};

export const DEMO_SHEETS = ["mood", "skin", "help", "feedback", "studio", "billing"];

function readQuery(search) {
  return new URLSearchParams(String(search || ""));
}

// An explicit ?tier= wins over the page's baked-in default
// (popupDemoFree.html sets window.__SLOWED_DEMO_TIER__ = "free"), so the
// Flows canvas can load either page in any state. Unknown values fall back.
export function resolveDemoTierKey({ search, windowTier } = {}) {
  const fromQuery = readQuery(search).get("tier");
  if (fromQuery && DEMO_TIERS[fromQuery]) return fromQuery;
  if (windowTier && DEMO_TIERS[windowTier]) return windowTier;
  return "unlocked";
}

// Everything a Flows frame can pin through the query string:
//   tier       unlocked | free | capped | trial | pro | lifetime
//   sheet      mood | skin | help | feedback | studio | billing (opens on load)
//   skin       any known skin (overrides the tier's)
//   promoLabel cap-offer label; qualifies the offer so the pill rotates to it
//   testMode   "true"
//   unsupported "1" shows the off-YouTube gate instead of the controls
//   soundcloud "access" shows the gate asking to run on SoundCloud,
//              "1" the controls as they are on a SoundCloud tab
//   spotify    the same for Spotify
export function resolveDemoParams({ search, windowTier } = {}) {
  const query = readQuery(search);
  const tierKey = resolveDemoTierKey({ search, windowTier });
  const props = { ...DEMO_TIERS[tierKey] };

  const skin = query.get("skin");
  if (skin && SKINS.includes(skin)) props.skin = skin;

  const sheet = query.get("sheet");
  const initialSheet = DEMO_SHEETS.includes(sheet) ? sheet : undefined;

  const promoLabel = query.get("promoLabel");
  if (promoLabel) {
    // Same shape the background hands the popup (dashboard usagePromo), with
    // this install already past `afterCaps` so the offer is qualified.
    props.usagePromo = {
      promoId: "flows-demo",
      label: promoLabel.slice(0, 60),
      afterCaps: 1,
    };
    props.weeklyCapCount = 1;
  }

  const accessSite = ["soundcloud", "spotify"].find(
    (site) => query.get(site) === "access",
  ) || null;

  return {
    tierKey,
    ...props,
    initialSheet,
    ratePrompt: query.get("rate") === "1",
    updateBanner: query.get("update") === "1",
    // Off YouTube (?unsupported=1), the link to Slowed for any audio file.
    toolUrl: query.get("tool") === "1" ? "https://studio.slowed.app/" : null,
    testMode: query.get("testMode") === "true",
    notSupported: query.get("unsupported") === "1" || accessSite !== null,
    accessSite,
    ...(query.get("soundcloud") === "1" ? { hasVideo: false } : {}),
    // Spotify: Slow Control alone, no effects and no Moods.
    ...(query.get("spotify") === "1"
      ? { hasVideo: false, hasEffects: false, spotifyBeta: true }
      : {}),
  };
}

export function resolveDemoTier() {
  const hasWindow = typeof window !== "undefined";
  const key = resolveDemoTierKey({
    search: hasWindow ? window.location.search : "",
    windowTier: hasWindow ? window.__SLOWED_DEMO_TIER__ : undefined,
  });
  return DEMO_TIERS[key];
}
